// « Comptabilité » — the C6 Finance screen (docs/PMS-LEDGER-UI-BRIEF.md). Admin only: the tab shows for
// `fin.ledger.read`, the reads enforce it again (one gate per layer, none trusting the one above), and
// the screen itself says « Droit manquant » rather than rendering an empty page.
//
// S1c: « État du livre ». S1d: « Balance ». S1e: « Soldes clients ». C5: « Centralisation » (what macompta receives). The drill-down to invoice and payment
// detail follows (S1g); no panel is created before its increment.

import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapabilities, useCapability } from '@lemontaigu/platform-ui';
import { isTrialBook, TRIAL_BOOK_NOTICE, type LedgerReader } from '@lemontaigu/fin-core';
import { Balance } from './Balance.js';
import { Centralisation } from './Centralisation.js';
import { EtatDuLivre } from './EtatDuLivre.js';
import { SoldesClients } from './SoldesClients.js';
import { FinProvider, useFin } from './FinProvider.js';

/** The screen's body, given its port: what the tests render. */
export function ComptabiliteScreen({ ledger, book }: { ledger: LedgerReader; book: string }) {
  return (
    <div className="fin-screen" data-testid="fin-screen">
      <h2>Comptabilité</h2>
      {isTrialBook(book) && (
        <div className="fin-trial" role="note" data-testid="fin-trial-notice">
          {TRIAL_BOOK_NOTICE}
        </div>
      )}
      <EtatDuLivre ledger={ledger} book={book} />
      <Balance ledger={ledger} book={book} />
      <SoldesClients ledger={ledger} book={book} />
      <Centralisation ledger={ledger} book={book} />
    </div>
  );
}

function Body() {
  const fin = useFin();
  if (!fin) return <p className="muted" data-testid="fin-no-property">Aucun établissement résolu.</p>;
  return <ComptabiliteScreen ledger={fin.ledger} book={fin.book} />;
}

/** The mounted screen. Must sit inside <PmsProvider> (the reader is bound to the établissement in view). */
export function Comptabilite() {
  const allowed = useCapability(CAPABILITIES.FIN_LEDGER_READ);
  const { loading } = useCapabilities();
  if (loading) return <p className="muted">Chargement…</p>;
  if (!allowed) {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-forbidden">
        Droit manquant : la comptabilité est réservée à l’administrateur.
      </div>
    );
  }
  return (
    <FinProvider>
      <Body />
    </FinProvider>
  );
}
